import './timeTracker.css';
import { Outlet } from 'react-router-dom';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import userTypeConsts from '../../constants/userTypeConsts';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { setHeaderText } from "../../features/headerText/headerTextSlice";

// Helper function - moved outside component since it doesn't depend on component state or props
// Maps time tracker routes to their display text
const getOptionText = (path) => {
  try {
    if (typeof path !== "string") {
      console.error("Invalid path type:", path);
      return "Invalid Path";
    }

    switch (path) {
      case "/user/time-tracker/overview":
        return "Overview";
      case "/user/time-tracker/timer":
        return "Tracker";
      case "/user/time-tracker/tag":
        return "Tags";
      default:
        console.warn("Unknown path:", path);
        return "Unknown";
    }
  } catch (error) {
    console.error("Error in getOptionText:", error);
    return "Error";
  }
};

function TabModuleTracker() {
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const [selectedOption, setSelectedOption] = useState("Overview");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const optionDropdownRef = useRef(null);
  const dropdownListRef = useRef(null);

  // Get available options based on user role - memoized to prevent recreation on every render
  const getAvailableOptions = useMemo(() => {
    const options = [
      { text: "Overview", url: "/user/time-tracker/overview" },
      { text: "Tracker", url: "/user/time-tracker/timer" },
    ];
    if (user && (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN)) {
      options.push({ text: "Tags", url: "/user/time-tracker/tag" });
    }
    return options;
  }, [user]);

  // Event handlers
  const handleOptionClick = useCallback((option, url) => {
    setSelectedOption(option);
    navigate(url);
    setDropdownOpen(false);
    setFocusedIndex(-1);
  }, [navigate]);

  // Keyboard navigation handler for dropdown button - memoized to prevent recreation
  const handleDropdownKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setDropdownOpen(!dropdownOpen);
        if (!dropdownOpen) {
          setFocusedIndex(0);
        } else {
          setFocusedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setDropdownOpen(false);
        setFocusedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!dropdownOpen) {
          setDropdownOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex((prev) => (prev < getAvailableOptions.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (dropdownOpen) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : getAvailableOptions.length - 1));
        }
        break;
      default:
        break;
    }
  }, [dropdownOpen, getAvailableOptions]);

  // Keyboard navigation handler for list items - memoized to prevent recreation
  const handleListItemKeyDown = useCallback((e, index, option, url) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleOptionClick(option, url);
        break;
      case 'Escape':
        e.preventDefault();
        setDropdownOpen(false);
        setFocusedIndex(-1);
        optionDropdownRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(index < getAvailableOptions.length - 1 ? index + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(index > 0 ? index - 1 : getAvailableOptions.length - 1);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(getAvailableOptions.length - 1);
        break;
      default:
        break;
    }
  }, [handleOptionClick, getAvailableOptions]);

  // Focus management when dropdown opens/closes
  useEffect(() => {
    if (dropdownOpen && dropdownListRef.current && focusedIndex >= 0) {
      const listItems = dropdownListRef.current.querySelectorAll('li[role="option"]');
      if (listItems[focusedIndex]) {
        listItems[focusedIndex].focus();
      }
    }
  }, [dropdownOpen, focusedIndex]);

  useEffect(() => {
    const currentPath = location.pathname;
    const optionText = getOptionText(currentPath);
    setSelectedOption(optionText);
  }, [location.pathname]);


  useEffect(() => {
    const handleClick = (event) => {
      try {
        if (
          optionDropdownRef.current &&
          !optionDropdownRef.current.contains(event.target)
        ) {
          setDropdownOpen(false);
        }
      } catch (error) {
        console.error("Error in outside click handler:", error);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    dispatch(setHeaderText("Time Tracker"));
  }, [dispatch]);



  return (
    <div>
      <div className='tab-outer-time'>
        <div className='tab-mobile-select'>
          <h4>Select an option</h4>
          <div className="custom-dropdown-user-new-outer">
            <div
              className="selected-option custom-dropdown-user-new"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              ref={optionDropdownRef}
              role="button"
              aria-label="Select time tracker option"
              aria-expanded={dropdownOpen}
              aria-haspopup="listbox"
              tabIndex={0}
              onKeyDown={handleDropdownKeyDown}
            >
              <span>{selectedOption}</span>
              <svg width="18" height="9" viewBox="0 0 18 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-chevron-icon" aria-hidden="true">
                <path d="M1.5 1.5L9 7.5L16.5 1.5" stroke="#405BFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {dropdownOpen && (
              <div className="custom-dropdown-new" role="listbox" ref={dropdownListRef}>
                <ul className="showing-options-list">
                  <li
                    role="option"
                    aria-selected={selectedOption === "Overview"}
                    tabIndex={focusedIndex === 0 ? 0 : -1}
                    onClick={() => handleOptionClick("Overview", "/user/time-tracker/overview")}
                    onKeyDown={(e) => handleListItemKeyDown(e, 0, "Overview", "/user/time-tracker/overview")}
                  >
                    Overview
                  </li>
                  <li
                    role="option"
                    aria-selected={selectedOption === "Tracker"}
                    tabIndex={focusedIndex === 1 ? 0 : -1}
                    onClick={() => handleOptionClick("Tracker", "/user/time-tracker/timer")}
                    onKeyDown={(e) => handleListItemKeyDown(e, 1, "Tracker", "/user/time-tracker/timer")}
                  >
                    Tracker
                  </li>
                  {user && (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && (
                    <li
                      role="option"
                      aria-selected={selectedOption === "Tags"}
                      tabIndex={focusedIndex === 2 ? 0 : -1}
                      onClick={() => handleOptionClick("Tags", "/user/time-tracker/tag")}
                      onKeyDown={(e) => handleListItemKeyDown(e, 2, "Tags", "/user/time-tracker/tag")}
                    >
                      Tags
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        <ul className="tabs">
          <li>
            <NavLink
              to="/user/time-tracker/overview"
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              Overview
            </NavLink>
          </li>

          <li>
            <NavLink
              to='/user/time-tracker/timer'
              className={({ isActive }) => isActive ? 'active' : ''}
            >
              Tracker
            </NavLink>
          </li>
          {user && (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN) && (
            <li>
              <NavLink
                to='/user/time-tracker/tag'
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                Tags
              </NavLink>
            </li>
          )}
        </ul>

        <div className="tabs-content">
          <div className='tab-inner-time'>
            <Outlet />
          </div>
        </div>

      </div>
    </div>
  );
}

export default TabModuleTracker;
